import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Shuffle, Sparkles, Save, ArrowLeft, ArrowRight } from 'lucide-react';
import { AvatarDisplay } from './AvatarDisplay';
import { 
  AVATAR_CATEGORIES, 
  AVATAR_COLORS, 
  AVATAR_ANIMATIONS, 
  DEFAULT_AVATAR_CONFIG,
  generateAvatarFromUsername,
  type AvatarConfig 
} from '@/lib/avatar-config';
import { cn } from '@/lib/utils';

interface AvatarCreatorProps {
  initialConfig?: AvatarConfig;
  username?: string;
  onSave: (config: AvatarConfig) => void;
  onCancel?: () => void;
}

export const AvatarCreator = ({ 
  initialConfig = DEFAULT_AVATAR_CONFIG, 
  username = 'Player',
  onSave, 
  onCancel 
}: AvatarCreatorProps) => {
  const [currentConfig, setCurrentConfig] = useState<AvatarConfig>(initialConfig);
  const [step, setStep] = useState<'category' | 'style' | 'colors' | 'animations'>('category');
  const [showPreview, setShowPreview] = useState(true);

  const updateConfig = (updates: Partial<AvatarConfig>) => {
    setCurrentConfig(prev => ({ ...prev, ...updates }));
  };

  const randomizeAvatar = () => {
    const randomConfig = generateAvatarFromUsername(Math.random().toString());
    setCurrentConfig(randomConfig);
  };

  const generateFromUsername = () => {
    const config = generateAvatarFromUsername(username);
    setCurrentConfig(config);
  };

  const nextStep = () => {
    const steps = ['category', 'style', 'colors', 'animations'] as const;
    const currentIndex = steps.indexOf(step);
    if (currentIndex < steps.length - 1) {
      setStep(steps[currentIndex + 1]);
    }
  };

  const prevStep = () => {
    const steps = ['category', 'style', 'colors', 'animations'] as const;
    const currentIndex = steps.indexOf(step);
    if (currentIndex > 0) {
      setStep(steps[currentIndex - 1]);
    }
  };

  const renderCategoryStep = () => (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-foreground mb-2">Choose Your Vibe</h3>
        <p className="text-muted-foreground">Pick a category that matches your style</p>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        {Object.entries(AVATAR_CATEGORIES).map(([key, category]) => (
          <Card 
            key={key}
            className={cn(
              'cursor-pointer transition-all duration-200 hover:scale-105',
              currentConfig.category === key 
                ? 'ring-2 ring-teen-blue bg-teen-blue/10' 
                : 'hover:bg-muted/50'
            )}
            onClick={() => updateConfig({ category: key as any })}
          >
            <CardContent className="p-4 text-center">
              <div className="text-2xl mb-2">{category.name.split(' ')[0]}</div>
              <h4 className="font-medium text-sm">{category.name.split(' ')[1]}</h4>
              <p className="text-xs text-muted-foreground mt-1">{category.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  const renderStyleStep = () => (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-foreground mb-2">Pick Your Style</h3>
        <p className="text-muted-foreground">Choose from {AVATAR_CATEGORIES[currentConfig.category].name} styles</p>
      </div>
      
      <div className="grid grid-cols-2 gap-3">
        {AVATAR_CATEGORIES[currentConfig.category].styles.map((style) => (
          <Button
            key={style}
            variant={currentConfig.style === style ? 'default' : 'outline'}
            className={cn(
              'h-16 text-center capitalize',
              currentConfig.style === style && 'bg-teen-purple text-white'
            )}
            onClick={() => updateConfig({ style })}
          >
            <div>
              <div className="text-lg">{style}</div>
            </div>
          </Button>
        ))}
      </div>
    </div>
  );

  const renderColorsStep = () => (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-foreground mb-2">Color Combo</h3>
        <p className="text-muted-foreground">Make it pop with your favorite colors</p>
      </div>
      
      <div className="space-y-4">
        {(['primary', 'secondary', 'accent'] as const).map((colorType) => (
          <div key={colorType}>
            <label className="block text-sm font-medium mb-2 capitalize">{colorType} Color</label>
            <div className="grid grid-cols-4 gap-2">
              {Object.entries(AVATAR_COLORS.teen).map(([colorName, colorValue]) => (
                <button
                  key={colorName}
                  className={cn(
                    'w-12 h-12 rounded-full border-2 transition-all duration-200',
                    currentConfig.colors[colorType] === colorValue
                      ? 'border-foreground scale-110 ring-2 ring-teen-gold'
                      : 'border-muted-foreground/30 hover:scale-105'
                  )}
                  style={{ backgroundColor: colorValue }}
                  onClick={() => updateConfig({ 
                    colors: { ...currentConfig.colors, [colorType]: colorValue }
                  })}
                  title={colorName}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderAnimationsStep = () => (
    <div className="space-y-4">
      <div className="text-center">
        <h3 className="text-lg font-semibold text-foreground mb-2">Add Some Life</h3>
        <p className="text-muted-foreground">Choose animations for different moods</p>
      </div>
      
      <div className="space-y-4">
        {(['idle', 'celebration', 'thinking'] as const).map((animType) => (
          <div key={animType}>
            <label className="block text-sm font-medium mb-2 capitalize">{animType} Animation</label>
            <div className="flex flex-wrap gap-2">
              {AVATAR_ANIMATIONS[animType].map((anim) => (
                <Badge
                  key={anim}
                  variant={currentConfig.animations[animType] === anim ? 'default' : 'secondary'}
                  className={cn(
                    'cursor-pointer hover:scale-105 transition-transform',
                    currentConfig.animations[animType] === anim && 'bg-teen-green text-white'
                  )}
                  onClick={() => updateConfig({
                    animations: { ...currentConfig.animations, [animType]: anim }
                  })}
                >
                  {anim}
                </Badge>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case 'category': return renderCategoryStep();
      case 'style': return renderStyleStep();
      case 'colors': return renderColorsStep();
      case 'animations': return renderAnimationsStep();
      default: return renderCategoryStep();
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold bg-gradient-to-r from-teen-blue to-teen-purple bg-clip-text text-transparent">
          Avatar Creator
        </h1>
        <p className="text-muted-foreground">Create your unique gaming persona</p>
      </div>

      {/* Progress */}
      <div className="flex justify-center">
        <div className="flex items-center space-x-2">
          {['category', 'style', 'colors', 'animations'].map((s, i) => (
            <div key={s} className="flex items-center">
              <div className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
                step === s ? 'bg-teen-blue text-white' : 'bg-muted text-muted-foreground'
              )}>
                {i + 1}
              </div>
              {i < 3 && <div className="w-8 h-0.5 bg-muted mx-2" />}
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Preview */}
        <Card className="lg:order-2">
          <CardHeader className="text-center">
            <CardTitle className="text-lg">Preview</CardTitle>
            <CardDescription>How your avatar will look</CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <AvatarDisplay
              username={username}
              avatarConfig={currentConfig}
              size="xl"
              showAnimation={showPreview}
              className="mx-auto"
            />
            <div className="space-y-2">
              <p className="font-medium">{username}</p>
              <Badge variant="outline" className="capitalize">
                {currentConfig.category.replace('-', ' ')} • {currentConfig.style}
              </Badge>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPreview(!showPreview)}
              className="w-full"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              {showPreview ? 'Stop' : 'Play'} Animation
            </Button>
          </CardContent>
        </Card>

        {/* Creator */}
        <Card className="lg:order-1">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="capitalize">{step.replace('-', ' ')} Selection</CardTitle>
                <CardDescription>Step {['category', 'style', 'colors', 'animations'].indexOf(step) + 1} of 4</CardDescription>
              </div>
              <Button variant="outline" size="sm" onClick={randomizeAvatar}>
                <Shuffle className="w-4 h-4 mr-2" />
                Surprise Me!
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {renderStep()}
          </CardContent>
        </Card>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <Button 
          variant="outline" 
          onClick={step === 'category' ? onCancel : prevStep}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {step === 'category' ? 'Cancel' : 'Back'}
        </Button>

        <Button variant="outline" onClick={generateFromUsername}>
          Generate from Username
        </Button>

        {step === 'animations' ? (
          <Button onClick={() => onSave(currentConfig)}>
            <Save className="w-4 h-4 mr-2" />
            Save Avatar
          </Button>
        ) : (
          <Button onClick={nextStep}>
            Next
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        )}
      </div>
    </div>
  );
};